import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Scale, Plus, Archive, CheckCircle2, RotateCcw, FileText, ChevronRight, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

/**
 * Institutional Doctrine admin page.
 *
 * Doctrine is CONSTITUTIONAL — loaded automatically before every AI Staff LLM call.
 * Only the Founder may create, activate, or supersede directives.
 */
export default function Doctrine() {
  const [directives, setDirectives] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("doctrine"); // doctrine | templates
  const [expanded, setExpanded] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([
        api.get("/doctrine"),
        api.get("/institutional-templates"),
      ]);
      setDirectives(d.data);
      setTemplates(t.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const activate = async (id) => {
    try { await api.post(`/doctrine/${id}/activate`); toast.success("Directive activated. Grant Writer will now load it on next execution."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this directive? It will no longer be loaded by any AI Staff.")) return;
    try { await api.post(`/doctrine/${id}/archive`); toast.success("Directive archived."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="doctrine-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Institution · Constitutional Layer</div>
          <h1 className="font-serif text-4xl mt-1">Institutional Doctrine</h1>
          <p className="text-sm text-ink-soft mt-2 max-w-2xl leading-relaxed">
            Every AI Staff member automatically loads the active Doctrine before analyzing or drafting. Directives are versioned. Only the Founder may create, activate, or supersede them. Conversation memory never overrides Doctrine.
          </p>
        </div>
        {tab === "doctrine" && (
          <Button onClick={() => setCreating(true)} data-testid="new-directive" className="bg-forest text-bone hover:bg-ink rounded-full">
            <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> New Directive
          </Button>
        )}
        {tab === "templates" && <NewTemplateButton onCreated={load} />}
      </div>

      <div className="flex gap-4 border-b border-line">
        <TabBtn active={tab === "doctrine"} onClick={() => setTab("doctrine")} label={`Directives · ${directives.length}`} testId="tab-doctrine" />
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} label={`Templates · ${templates.length}`} testId="tab-templates" />
      </div>

      {loading ? (
        <div className="text-sm text-ink-soft">Loading…</div>
      ) : tab === "doctrine" ? (
        <div className="space-y-3">
          {directives.length === 0 ? (
            <div className="text-sm text-ink-soft italic text-center py-8">No directives yet.</div>
          ) : directives.map(d => (
            <DirectiveCard
              key={d.id} d={d}
              expanded={expanded === d.id}
              onToggle={() => setExpanded(expanded === d.id ? null : d.id)}
              onActivate={() => activate(d.id)}
              onArchive={() => archive(d.id)}
            />
          ))}
        </div>
      ) : (
        <TemplatesList templates={templates} onChange={load} />
      )}

      {creating && <NewDirectiveModal onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}

function TabBtn({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick} data-testid={testId}
      className={`py-2 px-1 text-xs uppercase tracking-widest border-b-2 transition-colors ${
        active ? "border-terracotta text-terracotta" : "border-transparent text-ink-soft hover:text-ink"
      }`}
    >
      {label}
    </button>
  );
}

function DirectiveCard({ d, expanded, onToggle, onActivate, onArchive }) {
  const statusTone = {
    active: "bg-forest text-bone",
    draft: "bg-bone text-ink border border-line",
    superseded: "bg-line text-ink-soft",
    archived: "bg-line text-ink-soft",
  }[d.status] || "bg-line text-ink-soft";
  return (
    <div className="border border-line bg-admin-surface rounded-xl overflow-hidden" data-testid={`directive-${d.directive_id}-v${d.version}`}>
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center gap-4 hover:bg-bone/40 transition-colors text-left">
        <div className="w-10 h-10 rounded-full bg-ink text-bone flex items-center justify-center font-serif text-sm">
          <Scale className="h-4 w-4" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-ink">Directive #{d.directive_id} · {d.title}</div>
          <div className="text-xs text-ink-soft mt-0.5">
            v{d.version} · {(d.rules || []).length} rules · scope: {d.scope} · authored by {d.authored_by}
            {d.activated_at && ` · activated ${new Date(d.activated_at).toLocaleDateString()}`}
          </div>
        </div>
        <Badge className={`text-[10px] uppercase tracking-widest ${statusTone}`}>{d.status}</Badge>
        <ChevronRight className={`h-4 w-4 text-ink-soft transition-transform ${expanded ? "rotate-90" : ""}`} strokeWidth={1.5} />
      </button>
      {expanded && (
        <div className="px-5 py-4 border-t border-line bg-bone/20 space-y-4">
          {d.preamble && (
            <div className="text-sm text-ink italic leading-relaxed">{d.preamble}</div>
          )}
          <ol className="space-y-3">
            {(d.rules || []).map((r) => (
              <li key={`r-${r.number}`} className="border-l-2 border-terracotta pl-3">
                <div className="text-sm font-medium text-ink">Rule {r.number} — {r.title}</div>
                <div className="text-sm text-ink-soft leading-relaxed mt-0.5">{r.body}</div>
              </li>
            ))}
          </ol>
          <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
            {d.status === "draft" && (
              <Button onClick={onActivate} className="bg-forest text-bone hover:bg-ink rounded-full" data-testid={`activate-${d.id}`}>
                <CheckCircle2 className="h-4 w-4 mr-2" strokeWidth={1.5} /> Activate
              </Button>
            )}
            {(d.status === "active" || d.status === "draft") && (
              <Button variant="outline" onClick={onArchive} className="border-line" data-testid={`archive-${d.id}`}>
                <Archive className="h-4 w-4 mr-2" strokeWidth={1.5} /> Archive
              </Button>
            )}
            {d.status === "superseded" && d.superseded_at && (
              <span className="text-xs text-ink-soft italic">Superseded {new Date(d.superseded_at).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NewDirectiveModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    directive_id: "", title: "", preamble: "", scope: "grant_writer",
  });
  const [rules, setRules] = useState([{ number: 1, title: "", body: "" }]);
  const [saving, setSaving] = useState(false);

  const addRule = () => setRules([...rules, { number: rules.length + 1, title: "", body: "" }]);
  const removeRule = (idx) => setRules(rules.filter((_, i) => i !== idx).map((r, i) => ({ ...r, number: i + 1 })));
  const updateRule = (idx, patch) => setRules(rules.map((r, i) => i === idx ? { ...r, ...patch } : r));

  const save = async () => {
    if (!form.directive_id || !form.title) return toast.error("Directive ID and title are required.");
    if (rules.some(r => !r.title || !r.body)) return toast.error("Every rule needs a title and body.");
    setSaving(true);
    try {
      await api.post("/doctrine", { ...form, rules });
      toast.success(`Directive #${form.directive_id} created as draft. Activate it to make it constitutional.`);
      onCreated();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-admin-surface border border-line rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="new-directive-modal">
        <div className="px-6 py-4 border-b border-line">
          <div className="overline">New Directive</div>
          <h3 className="font-serif text-2xl text-ink mt-1">Author a Founder Directive</h3>
          <p className="text-xs text-ink-soft mt-1">This will be saved as a draft. Activate when ready to make it constitutional for all future Grant Writer executions.</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-ink-soft">Directive ID (e.g. 002)</label>
              <Input value={form.directive_id} onChange={e => setForm({...form, directive_id: e.target.value})} data-testid="directive-id-input" />
            </div>
            <div>
              <label className="text-xs text-ink-soft">Scope</label>
              <select value={form.scope} onChange={e => setForm({...form, scope: e.target.value})} data-testid="directive-scope-input" className="w-full h-10 border border-line rounded-md px-3 text-sm bg-bone">
                <option value="grant_writer">Grant Writer</option>
                <option value="global">Global (all staff)</option>
                <option value="volunteer_coordinator">Volunteer Coordinator</option>
                <option value="media">Media</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-ink-soft">Title</label>
            <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="directive-title-input" />
          </div>
          <div>
            <label className="text-xs text-ink-soft">Preamble (optional)</label>
            <Textarea rows={2} value={form.preamble} onChange={e => setForm({...form, preamble: e.target.value})} data-testid="directive-preamble-input" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-ink-soft">Rules</label>
              <button onClick={addRule} className="text-xs text-terracotta hover:underline" data-testid="add-rule">+ Add rule</button>
            </div>
            <div className="space-y-3">
              {rules.map((r, i) => (
                <div key={`rule-${i}`} className="border border-line rounded p-3 space-y-2 bg-bone/30">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-ink">Rule {r.number}</span>
                    {rules.length > 1 && <button onClick={() => removeRule(i)} className="text-xs text-ink-soft hover:text-terracotta" data-testid={`remove-rule-${i}`}>Remove</button>}
                  </div>
                  <Input placeholder="Rule title" value={r.title} onChange={e => updateRule(i, { title: e.target.value })} data-testid={`rule-title-${i}`} />
                  <Textarea rows={2} placeholder="Rule body — the constitutional text" value={r.body} onChange={e => updateRule(i, { body: e.target.value })} data-testid={`rule-body-${i}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-line flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-line">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-directive" className="bg-forest text-bone hover:bg-ink rounded-full">
            Save as draft
          </Button>
        </div>
      </div>
    </div>
  );
}

function TemplatesList({ templates, onChange }) {
  const [editing, setEditing] = useState(null);
  return (
    <div className="space-y-3" data-testid="templates-list">
      {templates.length === 0 ? (
        <div className="text-sm text-ink-soft italic text-center py-8">
          No institutional templates yet. Add Organization Summary, Fiscal Sponsor Explanation, Leadership Summary, Budget Narrative, Project Narrative, Evaluation Plan, Sustainability Plan, Letters of Support — the Grant Writer will pull them into every draft.
        </div>
      ) : templates.map(t => (
        <TemplateRow key={t.id} t={t} onEdit={() => setEditing(t)} />
      ))}
      {editing && <EditTemplateModal template={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChange(); }} />}
    </div>
  );
}

function TemplateRow({ t, onEdit }) {
  return (
    <div className="border border-line bg-admin-surface rounded-xl p-4 flex items-start gap-4" data-testid={`template-${t.slug}`}>
      <FileText className="h-5 w-5 text-terracotta mt-1" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-serif text-lg text-ink">{t.title}</div>
          <Badge className={`text-[10px] uppercase tracking-widest ${t.mode === "verbatim" ? "bg-ink text-bone" : "bg-terracotta/10 text-terracotta border border-terracotta/30"}`}>{t.mode}</Badge>
          <Badge className="bg-bone text-ink border border-line text-[10px]">{t.status}</Badge>
        </div>
        <div className="text-xs text-ink-soft mt-1">slug: <code className="text-terracotta">{t.slug}</code></div>
        {t.description && <p className="text-sm text-ink-soft mt-2">{t.description}</p>}
        <div className="text-sm text-ink mt-2 whitespace-pre-wrap line-clamp-3">{t.body}</div>
      </div>
      <Button variant="outline" size="sm" onClick={onEdit} className="border-line" data-testid={`edit-template-${t.slug}`}>Edit</Button>
    </div>
  );
}

function NewTemplateButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="new-template" className="bg-forest text-bone hover:bg-ink rounded-full">
        <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} /> New Template
      </Button>
      {open && <EditTemplateModal template={null} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); onCreated(); }} />}
    </>
  );
}

function EditTemplateModal({ template, onClose, onSaved }) {
  const [form, setForm] = useState(template || {
    slug: "", title: "", description: "", mode: "adapted", body: "", tags: [], status: "active",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.slug || !form.title || !form.body) return toast.error("Slug, title, and body are required.");
    setSaving(true);
    try {
      if (template) {
        await api.patch(`/institutional-templates/${template.id}`, form);
        toast.success("Template updated.");
      } else {
        await api.post("/institutional-templates", form);
        toast.success("Template created and active.");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-admin-surface border border-line rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="template-modal">
        <div className="px-6 py-4 border-b border-line">
          <div className="overline">{template ? "Edit template" : "New template"}</div>
          <h3 className="font-serif text-2xl text-ink mt-1">{form.title || "Institutional Template"}</h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-md bg-terracotta/10 border border-terracotta/30 px-3 py-2 text-xs text-terracotta flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5" strokeWidth={1.5} />
            <div>
              <strong>Verbatim</strong> templates (Fiscal Sponsor Explanation, Organization Summary, Leadership Summary) are quoted word-for-word in every draft.
              <strong> Adapted</strong> templates (Project Narrative, Evaluation Plan, Sustainability Plan) are used as scaffolding that the Grant Writer adapts to each grant.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-ink-soft">Slug (unique)</label>
              <Input value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} disabled={!!template} data-testid="template-slug" />
            </div>
            <div>
              <label className="text-xs text-ink-soft">Mode</label>
              <select value={form.mode} onChange={e => setForm({...form, mode: e.target.value})} data-testid="template-mode" className="w-full h-10 border border-line rounded-md px-3 text-sm bg-bone">
                <option value="verbatim">Verbatim (quoted word-for-word)</option>
                <option value="adapted">Adapted (scaffolding)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-ink-soft">Title</label>
            <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="template-title" />
          </div>
          <div>
            <label className="text-xs text-ink-soft">Description (optional)</label>
            <Input value={form.description || ""} onChange={e => setForm({...form, description: e.target.value})} data-testid="template-description" />
          </div>
          <div>
            <label className="text-xs text-ink-soft">Body — the template text</label>
            <Textarea rows={12} value={form.body} onChange={e => setForm({...form, body: e.target.value})} data-testid="template-body" />
          </div>
          {template && (
            <div>
              <label className="text-xs text-ink-soft">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} data-testid="template-status" className="w-full h-10 border border-line rounded-md px-3 text-sm bg-bone">
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-line flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="border-line">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-template" className="bg-forest text-bone hover:bg-ink rounded-full">
            {template ? "Save changes" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}
